import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Empty, Input, Modal, Row, Select, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { TEST_DIFFICULTY_OPTIONS } from '../constants/testDifficulty';
import { CumulativeMonth, CumulativeStatisticsData } from '../types/cumulativeStatistics';
import './CumulativeStatistics.css';
import MonthlyAssessment from './MonthlyAssessment';

const { Text, Paragraph, Title } = Typography;
const percent = (value: number | null) => value === null ? '—' : `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
const monthLabel = (value: string) => dayjs(`${value}-01`).format('MM.YYYY');
const changeLabel = (value: number | null) => value === null ? '—' : `${value > 0 ? '+' : ''}${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} п.п.`;
const metadataOptions = (values: string[]) => values.map(value => ({ value, label: value || 'Не указан' }));

function MethodologyContent({ data }: { data: CumulativeStatisticsData }) {
  return <>
    {data.methodology.map(section => <section key={section.title}>
      <Title level={5}>{section.title}</Title>
      <Paragraph>{section.description}</Paragraph>
    </section>)}
    <Space wrap>
      <Tag>Без даты завершения: {data.quality.undated_attempts_count}</Tag>
      <Tag>Без корректной оценки: {data.quality.unscored_attempts_count}</Tag>
      <Tag>Без уровня сложности: {data.quality.unspecified_difficulty_attempts_count}</Tag>
    </Space>
    <Paragraph className="cumulative-note">Данные рассчитаны: {dayjs(`${data.generated_at}Z`).format('DD.MM.YYYY HH:mm')}. Методика также включена в Excel отдельным листом.</Paragraph>
  </>;
}

function CumulativeChart({ months }: { months: CumulativeMonth[] }) {
  const width = Math.max(640, months.length * 64);
  const height = 240;
  const left = 60;
  const top = 24;
  const bottom = height - 40;
  const maximum = Math.max(1, ...months.map(item => item.cumulative_attempts));
  const points = months.map((item, index) => ({
    x: months.length === 1 ? width / 2 : left + index * (width - left - 32) / (months.length - 1),
    y: bottom - item.cumulative_attempts / maximum * (bottom - top),
    item,
  }));
  return (
    <div className="cumulative-chart" tabIndex={0} aria-label="График накопительных прохождений, прокрутка по горизонтали">
      <svg viewBox={`0 0 ${width} ${height}`} style={{ minWidth: width }} role="img" aria-label="Количество завершённых прохождений с начала истории по месяцам. Точные значения приведены в таблице ниже.">
        {[0, 0.5, 1].map(ratio => {
          const y = bottom - ratio * (bottom - top);
          return <g key={ratio}>
            <line x1={left} x2={width - 24} y1={y} y2={y} className="cumulative-chart-grid" />
            <text x={left - 8} y={y + 4} textAnchor="end">{Math.round(maximum * ratio).toLocaleString('ru-RU')}</text>
          </g>;
        })}
        <polyline points={points.map(point => `${point.x},${point.y}`).join(' ')} className="cumulative-chart-line" />
        {points.map(({ x, y, item }) => <g key={item.month}>
          <circle cx={x} cy={y} r={5} className="cumulative-chart-point">
            <title>{monthLabel(item.month)}: {item.cumulative_attempts} прохождений, {item.cumulative_participants} участников</title>
          </circle>
          <text x={x} y={height - 12} textAnchor="middle">{monthLabel(item.month)}</text>
        </g>)}
      </svg>
    </div>
  );
}

export default function CumulativeStatistics({ active }: { active: boolean }) {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(11, 'month').startOf('month'), dayjs().startOf('month')]);
  const [filters, setFilters] = useState<{ department?: string; region?: string; work_experience?: string; test_id?: number; difficulty?: string; report_group?: string }>({});
  const [assessmentMonths, setAssessmentMonths] = useState<1 | 3>(1);
  const [data, setData] = useState<CumulativeStatisticsData | null>(null);
  const [options, setOptions] = useState<CumulativeStatisticsData['options']>({ departments: [], regions: [], work_experiences: [], tests: [], report_groups: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [comparisonMonth, setComparisonMonth] = useState('');
  const [search, setSearch] = useState('');
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const exportBusy = useRef(false);
  const monthFrom = range[0].format('YYYY-MM-01');
  const monthTo = range[1].format('YYYY-MM-01');
  const requestParams = useMemo(() => ({ month_from: monthFrom, month_to: monthTo, assessment_months: assessmentMonths, ...filters }), [monthFrom, monthTo, assessmentMonths, filters]);

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    setError('');
    setExportError('');
    api.get<CumulativeStatisticsData>('/admin/statistics/cumulative', {
      params: requestParams,
      signal: controller.signal,
    }).then(response => {
      if (controller.signal.aborted) return;
      setData(response.data);
      setOptions(response.data.options);
      setComparisonMonth(current => response.data.timeline.some(item => item.month === current)
        ? current : response.data.timeline[response.data.timeline.length - 1]?.month || '');
    }).catch(reason => {
      if (!controller.signal.aborted) setError(getApiErrorMessage(reason, 'Не удалось загрузить накопительную статистику'));
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [active, requestParams, revision]);

  const downloadExcel = async () => {
    if (exportBusy.current || loading || !data) return;
    exportBusy.current = true;
    setExporting(true);
    setExportError('');
    let url: string | undefined;
    let anchor: HTMLAnchorElement | undefined;
    try {
      const response = await api.get('/admin/statistics/cumulative/export-excel', {
        params: { ...requestParams, participant_search: search.trim(), comparison_month: comparisonMonth || undefined },
        responseType: 'blob',
      });
      url = URL.createObjectURL(response.data);
      anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = /filename="([A-Za-z0-9_.-]+\.xlsx)"/.exec(response.headers['content-disposition'] || '')?.[1]
        || `cumulative_statistics_${monthFrom.slice(0, 7)}_${monthTo.slice(0, 7)}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
    } catch (reason: any) {
      let detail = reason;
      if (reason?.response?.data instanceof Blob) {
        try {
          detail = { ...reason, response: { ...reason.response, data: JSON.parse(await reason.response.data.text()) } };
        } catch { /* A non-JSON response uses the normal download error. */ }
      }
      setExportError(getApiErrorMessage(detail, 'Не удалось выгрузить накопительную статистику в Excel'));
    } finally {
      anchor?.remove();
      if (url) URL.revokeObjectURL(url);
      exportBusy.current = false;
      setExporting(false);
    }
  };

  const participants = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru-RU');
    return (data?.participants || []).filter(item =>
      [item.user_name, item.department, item.group_label].join(' ').toLocaleLowerCase('ru-RU').includes(query));
  }, [data, search]);
  const selectedMonth = data?.timeline.find(item => item.month === comparisonMonth);
  const comparison = selectedMonth?.comparison;
  const hasData = data && data.summary.attempts_count > 0;

  return (
    <Space direction="vertical" size="large" className="cumulative-statistics">
      <Card title="Накопительные итоги" extra={<Space wrap>
        <Button icon={<QuestionCircleOutlined />} disabled={!data || loading} onClick={() => setMethodologyOpen(true)}>Методика расчёта</Button>
        <Button icon={<DownloadOutlined />} disabled={!data || loading} loading={exporting} onClick={downloadExcel}
          title="Все разделы и все подходящие участники с текущими фильтрами; методика — отдельным листом">Выгрузить в Excel</Button>
        <Button onClick={() => setRevision(value => value + 1)} loading={loading}>Обновить</Button>
      </Space>}>
        <Paragraph>Оценка обязательного плана за месяц или три месяца и история прохождений накопительным итогом. Поздние пересдачи обновляют исходный месяц назначения.</Paragraph>
        <div className="cumulative-filters">
          <label><span>Период оценки</span><Select aria-label="Период оценки" value={assessmentMonths} onChange={setAssessmentMonths}
            options={[{ value: 1, label: '1 месяц' }, { value: 3, label: '3 месяца' }]} /></label>
          <label><span>Группа сотрудников</span><Select aria-label="Группа сотрудников" allowClear placeholder="Все группы"
            value={filters.report_group} options={options.report_groups || []} onChange={report_group => setFilters(current => ({ ...current, report_group }))} /></label>
          <label><span>Месяцы на графике</span><DatePicker.RangePicker
            picker="month" value={range} allowClear={false} format="MM.YYYY"
            onChange={value => { if (value?.[0] && value[1]) setRange([value[0], value[1]]); }}
          /></label>
          <label><span>Отдел</span><Select aria-label="Отдел" allowClear showSearch placeholder="Все отделы" optionFilterProp="label"
            value={filters.department} options={metadataOptions(options.departments)} onChange={department => setFilters(current => ({ ...current, department }))} /></label>
          <label><span>Регион</span><Select aria-label="Регион" allowClear showSearch placeholder="Все регионы" optionFilterProp="label"
            value={filters.region} options={metadataOptions(options.regions)} onChange={region => setFilters(current => ({ ...current, region }))} /></label>
          <label><span>Стаж работы</span><Select aria-label="Стаж работы" allowClear placeholder="Любой стаж"
            value={filters.work_experience} options={metadataOptions(options.work_experiences)} onChange={work_experience => setFilters(current => ({ ...current, work_experience }))} /></label>
          <label><span>Тест</span><Select aria-label="Тест" allowClear showSearch placeholder="Все тесты" optionFilterProp="label"
            value={filters.test_id} options={options.tests.map(test => ({ value: test.id, label: test.title }))} onChange={test_id => setFilters(current => ({ ...current, test_id }))} /></label>
          <label><span>Сложность</span><Select aria-label="Сложность" allowClear placeholder="Любая сложность"
            value={filters.difficulty} options={[...TEST_DIFFICULTY_OPTIONS, { value: 'unspecified', label: 'Не указана' }]}
            onChange={difficulty => setFilters(current => ({ ...current, difficulty }))} /></label>
          <label><span>Поиск в таблицах сотрудников</span><Input.Search aria-label="Поиск участника" placeholder="Имя, отдел, группа" value={search} maxLength={300} onChange={event => setSearch(event.target.value)} allowClear /></label>
        </div>
        <Button type="link" onClick={() => { setFilters({}); setAssessmentMonths(1); setRange([dayjs().subtract(11, 'month').startOf('month'), dayjs().startOf('month')]); setSearch(''); }}>Сбросить фильтры</Button>
        <Text type="secondary" className="cumulative-note">Оценка заканчивается последним месяцем диапазона и включает 1 или 3 месяца. Начало диапазона меняет историю на графике; более ранние прохождения остаются в накопительном итоге. Месяцы считаются по UTC.</Text>
      </Card>

      {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => setRevision(value => value + 1)}>Повторить</Button>} />}
      {exportError && <Alert type="error" showIcon message={exportError} action={<Button disabled={!data || loading} loading={exporting} onClick={downloadExcel}>Повторить выгрузку</Button>} />}
      {loading && <div className="cumulative-loading" role="status" aria-label="Загрузка накопительной статистики"><Spin size="large" /></div>}
      {data && !hasData && <Card><Empty description="Нет завершённых прохождений до конца выбранного месяца с этими фильтрами" /></Card>}
      {data && data.quality.undated_attempts_count > 0 && <Alert type="warning" showIcon
        message={`Завершённых попыток без даты: ${data.quality.undated_attempts_count}. Они исключены из расчётов по месяцам.`} />}
      {data?.assessment && <MonthlyAssessment data={data.assessment} search={search} />}

      {hasData && data && <>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Прохождений накопительно" value={data.summary.attempts_count} /><Text type="secondary">Включая все пересдачи</Text></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Участников накопительно" value={data.summary.participants_count} /><Text type="secondary">Уникальные сотрудники</Text></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Пройденных пар сотрудник — тест" value={data.summary.pairs_count} /><Text type="secondary">Повторных прохождений: {data.summary.retakes_count}</Text></Card></Col>
          <Col xs={24} sm={12} xl={6}><Card><Statistic title="Средний результат участников" value={data.summary.average_score ?? '—'} precision={2} suffix={data.summary.average_score === null ? undefined : '%'} /><Text type="secondary">По последним попыткам; равный вес участников</Text></Card></Col>
        </Row>

        <Card title="Как накапливаются прохождения">
          <CumulativeChart months={data.timeline} />
          <Table<CumulativeMonth> rowKey="month" dataSource={data.timeline} size="small" pagination={false} scroll={{ x: 940 }}
            columns={[
              { title: 'Месяц', dataIndex: 'month', render: monthLabel },
              { title: 'Прохождений за месяц', dataIndex: 'attempts_count' },
              { title: 'Участников за месяц', dataIndex: 'participants_count' },
              { title: 'Впервые участвовали', dataIndex: 'new_participants_count' },
              { title: 'Прохождений накопительно', dataIndex: 'cumulative_attempts' },
              { title: 'Участников накопительно', dataIndex: 'cumulative_participants' },
              { title: 'Средний результат накопительно', dataIndex: 'cumulative_average_score', render: percent },
            ]} />
        </Card>

        <Card title="Сравнение соседних месяцев на одинаковом составе">
          <Space wrap className="cumulative-comparison-picker"><Text>Месяц для сравнения</Text><Select
            aria-label="Месяц для сравнения" value={comparisonMonth} onChange={setComparisonMonth}
            options={data.timeline.map(item => ({ value: item.month, label: monthLabel(item.month) }))} />
            {comparison?.previous_month && <Text type="secondary">с {monthLabel(comparison.previous_month)}</Text>}
          </Space>
          {comparison && <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}><Statistic title="Сопоставимых участников" value={comparison.comparable_users_count} suffix={`/ ${comparison.current_users_count}`} /></Col>
              <Col xs={24} sm={8}><Statistic title="Общих пар сотрудник — тест с оценкой" value={comparison.common_pairs_count} /></Col>
              <Col xs={24} sm={8}><Statistic title="Изменение результата" value={changeLabel(comparison.delta_pp)} /></Col>
            </Row>
            <Paragraph className="cumulative-note">Прохождений: {comparison.previous_attempts_count} → {comparison.current_attempts_count}; участников: {comparison.previous_users_count} → {comparison.current_users_count}.
              {' '}Участвовали в обоих месяцах: {comparison.shared_users_count}; только в выбранном: {comparison.joined_users_count}; только в предыдущем: {comparison.left_users_count}.</Paragraph>
            {comparison.delta_pp === null ? <Alert type="info" showIcon message="Недостаточно сопоставимых данных"
              description="Нет одинаковых пар сотрудник — тест с оценкой в обоих месяцах. Изменение результата не рассчитывается." /> : <>
              <Paragraph>На общих тестах: <strong>{percent(comparison.previous_score)} → {percent(comparison.current_score)}</strong>.
                {' '}Сравнение охватывает {comparison.coverage_percent}% участников выбранного месяца.</Paragraph>
              {comparison.coverage_percent !== null && comparison.coverage_percent < 50 && <Alert type="warning" showIcon
                message="Сравнение охватывает менее половины участников выбранного месяца. Вывод относится к сопоставимой группе." />}
            </>}
          </>}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}><Card title="Состав по сложности тестов">
            <Table rowKey="difficulty" dataSource={data.difficulties} pagination={false} size="small" scroll={{ x: 540 }} columns={[
              { title: 'Сложность', dataIndex: 'label' },
              { title: 'Прохождений', dataIndex: 'attempts_count' },
              { title: 'Участников', dataIndex: 'participants_count' },
              { title: 'Тестов', dataIndex: 'tests_count' },
              { title: 'Результат', dataIndex: 'average_score', render: percent },
            ]} />
            <Text type="secondary" className="cumulative-note">Участник может входить в несколько уровней. Уровень берётся из текущих настроек теста; весовые коэффициенты сложности не применяются.</Text>
          </Card></Col>
          <Col xs={24} xl={12}><Card title="Состав по отделам">
            <Table rowKey="department" dataSource={data.departments} pagination={false} size="small" scroll={{ x: 540 }} columns={[
              { title: 'Отдел', dataIndex: 'department', render: value => value || 'Не указан' },
              { title: 'Участников', dataIndex: 'participants_count' },
              { title: 'Прохождений', dataIndex: 'attempts_count' },
              { title: 'Пересдач', dataIndex: 'retakes_count' },
              { title: 'Результат', dataIndex: 'average_score', render: percent },
            ]} />
            <Text type="secondary" className="cumulative-note">Сводка описывает состав выборки. Разный набор тестов ограничивает прямое сравнение отделов.</Text>
          </Card></Col>
        </Row>

        <Card title="Участники накопительного итога">
          <Paragraph type="secondary">Поиск меняет таблицы сотрудников, месячные итоги и их детализацию в Excel. Общие показатели не меняются. В файл попадут все найденные участники, включая остальные страницы таблиц.</Paragraph>
          <Table rowKey="user_id" dataSource={participants} size="small" scroll={{ x: 1000 }} pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: total => `Участников: ${total}` }} columns={[
            { title: 'Сотрудник', dataIndex: 'user_name', sorter: (a, b) => a.user_name.localeCompare(b.user_name, 'ru') },
            { title: 'Отдел', dataIndex: 'department', render: value => value || 'Не указан' },
            { title: 'Регион', dataIndex: 'region', render: value => value || 'Не указан' },
            { title: 'Стаж', dataIndex: 'work_experience', render: value => value || 'Не указан' },
            { title: 'Прохождений', dataIndex: 'attempts_count', sorter: (a, b) => a.attempts_count - b.attempts_count },
            { title: 'Разных тестов', dataIndex: 'tests_count' },
            { title: 'Пересдач', dataIndex: 'retakes_count' },
            { title: 'Результат', dataIndex: 'average_score', render: percent },
          ]} />
        </Card>

        <Card>
          <details className="cumulative-methodology">
            <summary>Методика расчёта и ограничения сравнения</summary>
            <MethodologyContent data={data} />
          </details>
        </Card>
      </>}
      <Modal title="Методика расчёта накопительной статистики" open={methodologyOpen} onCancel={() => setMethodologyOpen(false)}
        width={860} styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }} footer={<Button onClick={() => setMethodologyOpen(false)}>Понятно</Button>}>
        {data && <MethodologyContent data={data} />}
      </Modal>
    </Space>
  );
}
